/**
 * Toolbar above the page sheet in the signing canvas: document stepper +
 * picker on the left, page stepper on the right. Traced from the "Signing
 * View" frame of `Signer Experience.dc.html`.
 */
import { Box, ButtonBase, ClickAwayListener, Typography } from "@mui/material";
import { RequestFile } from "models/interfaces";
import { useState } from "react";
import { isTruthy } from "helpers/methods";
import { isFieldVisibleTo } from "screens/SignRequest/SignRequestHelper";
import tokens from "../SignerExperience.tokens";
import {
  CaretDownIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "./SignerExperienceIcons";

const { palette, canvas, docMenu } = tokens;

/** "12/Sample-Fillable-PDF.pdf" → "Sample-Fillable-PDF.pdf". */
export const getFileName = (file?: RequestFile | null) => {
  if (!file) return "";
  const raw = (file as any).unsignedDoc || (file as any).unsignedPdf || "";
  return raw.split("/")[1] ?? raw.split("/")[0] ?? "";
};

interface DocumentToolbarProps {
  files: RequestFile[];
  activeIndex: number;
  onPickDocument: (file: RequestFile, index: number) => void;
  /** Index of the signing participant inside `signRequest.signers`. */
  signerIndex: number;
  numPages: number;
  currentPage: number;
  onGoToPage: (page: number) => void;
  disabled?: boolean;
}

/** 34x34 outlined stepper button shared by the document arrows. */
const StepperButton = ({
  label,
  disabled,
  onClick,
  children,
  size,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
  size: number;
}) => (
  <ButtonBase
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: canvas.navButtonRadius,
      border: `1px solid ${palette.border}`,
      backgroundColor: palette.white,
      color: disabled ? palette.disabled : palette.body,
      cursor: disabled ? "default" : "pointer",
      "&:hover": { backgroundColor: disabled ? palette.white : palette.hover },
      "&.Mui-disabled": { color: palette.disabled },
      "&.Mui-focusVisible": {
        outline: `2px solid ${palette.accent}`,
        outlineOffset: "2px",
      },
    }}
  >
    {children}
  </ButtonBase>
);

const DocumentToolbar = ({
  files,
  activeIndex,
  onPickDocument,
  signerIndex,
  numPages,
  currentPage,
  onGoToPage,
  disabled,
}: DocumentToolbarProps) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const activeFile = files[activeIndex];
  const atFirstDoc = activeIndex <= 0;
  const atLastDoc = activeIndex >= files.length - 1;
  const pageCount = numPages || 0;

  const pickDocument = (index: number) => {
    setMenuOpen(false);
    if (index === activeIndex || disabled) return;
    onPickDocument(files[index], index);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: canvas.toolbarGap,
        mb: canvas.toolbarMarginBottom,
        // Sits above the scrolling sheet, not inside it — never compressed.
        flexShrink: 0,
      }}
    >
      <StepperButton
        label="Previous document"
        size={canvas.navButtonSize}
        disabled={atFirstDoc || !!disabled}
        onClick={() => pickDocument(activeIndex - 1)}
      >
        <ChevronLeftIcon size={canvas.navIconSize} />
      </StepperButton>

      <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
        <Box sx={{ position: "relative", minWidth: 0 }}>
          <ButtonBase
            onClick={() => setMenuOpen((open) => !open)}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={menuOpen}
            sx={{
              height: canvas.docButtonHeight,
              maxWidth: canvas.docButtonMaxWidth,
              px: canvas.docButtonPaddingX,
              borderRadius: canvas.docButtonRadius,
              border: `${canvas.docButtonBorderWidth} solid ${palette.border}`,
              backgroundColor: palette.white,
              color: palette.ink,
              fontFamily: "inherit",
              display: "flex",
              alignItems: "center",
              gap: canvas.docButtonGap,
              "&:hover": { borderColor: palette.borderStrong },
              "&.Mui-focusVisible": {
                outline: `2px solid ${palette.accent}`,
                outlineOffset: "2px",
              },
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: canvas.docButtonFontSize,
                fontWeight: canvas.docButtonFontWeight,
                color: "inherit",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {getFileName(activeFile)}
            </Typography>
            <Typography
              component="span"
              sx={{
                flexShrink: 0,
                fontSize: canvas.docPosFontSize,
                fontWeight: 700,
                color: palette.muted,
              }}
            >
              {activeIndex + 1} of {files.length}
            </Typography>
            <Box sx={{ flexShrink: 0, color: palette.muted, display: "flex" }}>
              <CaretDownIcon />
            </Box>
          </ButtonBase>

          {menuOpen && (
            <Box
              role="listbox"
              aria-label="Documents"
              sx={{
                position: "absolute",
                top: docMenu.top,
                left: 0,
                width: docMenu.width,
                maxHeight: docMenu.maxHeight,
                overflowY: "auto",
                backgroundColor: palette.white,
                border: `1px solid ${palette.borderLight}`,
                borderRadius: docMenu.radius,
                boxShadow: docMenu.shadow,
                p: docMenu.padding,
                zIndex: 50,
                "&::-webkit-scrollbar": { width: tokens.scrollbar.size },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: tokens.scrollbar.thumb,
                  borderRadius: tokens.scrollbar.radius,
                },
              }}
            >
              {files.map((file, index) => {
                const myFields = (file.fields || []).filter(
                  (field: any) =>
                    isFieldVisibleTo(field, signerIndex) && !field.hidden,
                );
                const done =
                  myFields.length > 0 &&
                  myFields.every((field: any) => isTruthy(field.value));
                const needsYou = myFields.length > 0 && !done;

                return (
                  <Box
                    key={getFileName(file) + index}
                    role="option"
                    aria-selected={index === activeIndex}
                    aria-label={`${getFileName(file)}${
                      done ? ", completed" : needsYou ? ", needs your signature" : ""
                    }`}
                    tabIndex={0}
                    onClick={() => pickDocument(index)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        pickDocument(index);
                      }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: docMenu.itemGap,
                      p: docMenu.itemPadding,
                      borderRadius: docMenu.itemRadius,
                      backgroundColor:
                        index === activeIndex ? palette.accentTint : palette.white,
                      cursor: "pointer",
                      "&:hover": { backgroundColor: palette.railRest },
                      "&:focus-visible": {
                        outline: `2px solid ${palette.accent}`,
                        outlineOffset: "-2px",
                      },
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        width: docMenu.numWidth,
                        flexShrink: 0,
                        fontSize: docMenu.numFontSize,
                        fontWeight: 700,
                        color: palette.muted,
                      }}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: docMenu.nameFontSize,
                        fontWeight: docMenu.nameFontWeight,
                        color: palette.ink,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {getFileName(file)}
                    </Typography>
                    {done && (
                      <Box sx={{ flexShrink: 0, color: palette.success, display: "flex" }}>
                        <CheckIcon size={docMenu.checkSize} />
                      </Box>
                    )}
                    {needsYou && (
                      <Typography
                        component="span"
                        sx={{
                          flexShrink: 0,
                          fontSize: docMenu.badgeFontSize,
                          fontWeight: 800,
                          color: palette.accentDark,
                          backgroundColor: palette.accentTint,
                          border: `1px solid ${palette.accentBorder}`,
                          borderRadius: docMenu.badgeRadius,
                          p: docMenu.badgePadding,
                        }}
                      >
                        Sign
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </ClickAwayListener>

      <StepperButton
        label="Next document"
        size={canvas.navButtonSize}
        disabled={atLastDoc || !!disabled}
        onClick={() => pickDocument(activeIndex + 1)}
      >
        <ChevronRightIcon size={canvas.navIconSize} />
      </StepperButton>

      <Box
        sx={{
          ml: "auto",
          display: "flex",
          alignItems: "center",
          gap: canvas.pageNavGap,
        }}
      >
        <StepperButton
          label="Previous page"
          size={canvas.pageButtonSize}
          disabled={currentPage <= 1}
          onClick={() => onGoToPage(currentPage - 1)}
        >
          <ChevronLeftIcon size={canvas.navIconSize} />
        </StepperButton>
        <Typography
          sx={{
            fontSize: canvas.pageLabelFontSize,
            fontWeight: canvas.pageLabelFontWeight,
            color: palette.body,
            px: canvas.pageLabelPaddingX,
            whiteSpace: "nowrap",
          }}
        >
          Page {pageCount ? currentPage : 0} / {pageCount}
        </Typography>
        <StepperButton
          label="Next page"
          size={canvas.pageButtonSize}
          disabled={currentPage >= pageCount}
          onClick={() => onGoToPage(currentPage + 1)}
        >
          <ChevronRightIcon size={canvas.navIconSize} />
        </StepperButton>
      </Box>
    </Box>
  );
};

export default DocumentToolbar;
